/* SIZESOFTX
=========================================================================================
* Descripción: Módulo de compras
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 14/05/2026 |                                   | Creación módulo compras
*      2       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Separación etiquetado manual en compras aplicadas
*      3       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Integración obligatoria a inventario al aplicar
*      4       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Bloqueo tabla lectura y guardado al salir de cantidad
*      5       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Selección destino almacén exhibidor para inventario
*      6       | 31/05/2026 | Lic. José Luis Aragón Cervantes    | Bloqueo cabecero en edición y vista previa centrada
*      7       | 01/06/2026 | Lic. José Luis Aragón Cervantes    | Retiro de atajos directos de eliminación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import Autocomplete from "../../ui/Autocomplete";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Box } from "@mui/material";

import { getDateStringV2 } from "../../../settings/utils.js";

import MnuComprasActions from "./MnuComprasActions";
import ModalBuscarArticulosSimple from "../../herramientas/ModalBuscarArticulosSimple";
import ModalBuscarCompras from "./ModalBuscarCompras";
import SizeSoftXConfirmDialog from "../../leyout/SizeSoftXConfirmDialog";
import SizeSoftXAlert from "../../utils/SizeSoftXAlert";
import SizeSoftXLayoutForm from "../../leyout/SizeSoftXLayoutForm";
import SizeSoftXTableVentas from "../../leyout/SizeSoftXTableVentas";
import SemaforoStatus from "../../utils/SemaforoStatus";
import SizeSoftXLoadingProcess from "../../leyout/SizeSoftXLoadingProcess";

import { useCompras } from "../../../context/Administracion/ComprasContext";
import { useInventarios } from "../../../context/Inventarios/InventariosContext";
import { useSesion } from "../../../context/System/SesionContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";

function FormulaCell({ row, onSave, onNavigate, disabled, opcionesCorrida }) {
  const [valor, setValor] = useState(row.corrida || "");
  const inputRef = useRef(null);
  const lastSavedValueRef = useRef(row.corrida || "");
  const valorRef = useRef(valor);
  useEffect(() => { valorRef.current = valor; }, [valor]);

  useEffect(() => {
    setValor(row.corrida || "");
    lastSavedValueRef.current = row.corrida || "";
  }, [row.corrida, row.id]);

  const triggerSave = async (newValue) => {
    const val = (newValue || "").trim();
    if (val !== lastSavedValueRef.current) {
      lastSavedValueRef.current = val;
      await onSave(row, { corrida: val });
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      await triggerSave(valor);
      onNavigate(row, e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(row.corrida || "");
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={row.corrida || ""}
        disabled
        inputProps={{ style: { textAlign: "center", padding: "4px 2px", width: "120px" } }}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={opcionesCorrida || []}
      value={valor}
      onChange={async (e, newValue) => {
        const val = newValue || "";
        setValor(val);
        valorRef.current = val;
        await triggerSave(val);
      }}
      onInputChange={(e, newValue) => {
        setValor(newValue || "");
        valorRef.current = newValue || "";
      }}
      onBlur={async () => {
        await triggerSave(valorRef.current);
      }}
      onKeyDown={handleKeyDown}
      renderInput={(params) => (
        <TextField
          autoComplete="off"
          {...params}
          inputRef={(el) => {
            inputRef.current = el;
            if (params.inputRef) params.inputRef.current = el;
          }}
          onClick={(e) => {
            if (e.target && e.target.select) e.target.select();
          }}
          onFocus={(e) => {
            setValor(row.corrida || "");
            setTimeout(() => {
              if (inputRef.current && inputRef.current.select) {
                inputRef.current.select();
              }
            }, 0);
          }}
          inputProps={{
            ...params.inputProps,
            "data-corrida-id": row.id,
            style: { textAlign: "center", padding: "4px 2px", width: "120px" },
          }}
        />
      )}
      sx={{ width: "130px" }}
    />
  );
}

function CantidadCell({ row, onSave, onNavigate, disabled, tipo }) {
  const valorCampo = tipo === "solicitada" ? row.cantidadsolicitada : row.cantidadrecibida;
  const [valor, setValor] = useState(String(valorCampo || 0));
  const inputRef = useRef(null);
  const valorRef = useRef(String(valorCampo || 0));
  const valorGuardadoRef = useRef(Number(valorCampo) || 0);

  useEffect(() => {
    const valorActual = String(valorCampo || 0);
    setValor(valorActual);
    valorRef.current = valorActual;
    valorGuardadoRef.current = Number(valorCampo) || 0;
  }, [valorCampo, row.id]);

  const guardarValor = async () => {
    const valorActual = valorRef.current;
    const nuevaCantidad = parseFloat(valorActual);
    if (valorActual !== "" && !isNaN(nuevaCantidad) && nuevaCantidad >= 0 && nuevaCantidad !== valorGuardadoRef.current) {
      valorGuardadoRef.current = nuevaCantidad;
      setValor(String(nuevaCantidad));
      valorRef.current = String(nuevaCantidad);
      await onSave(row, { [tipo === "solicitada" ? "cantidadsolicitada" : "cantidadrecibida"]: nuevaCantidad });
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      await guardarValor();
      onNavigate(row, e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      const valorAnterior = String(valorCampo || 0);
      setValor(valorAnterior);
      valorRef.current = valorAnterior;
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={valorCampo || 0}
        disabled
        inputProps={{ style: { textAlign: "center", padding: "4px 2px", width: "80px" } }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      inputRef={inputRef}
      value={valor}
      onClick={(e) => {
        if (e.target && e.target.select) e.target.select();
      }}
      onFocus={(e) => {
        setValor(String(valorCampo || 0));
        setTimeout(() => {
          if (inputRef.current && inputRef.current.select) {
            inputRef.current.select();
          }
        }, 0);
      }}
      onChange={(e) => {
        setValor(e.target.value);
        valorRef.current = e.target.value;
      }}
      onBlur={guardarValor}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 0,
        inputMode: "numeric",
        "data-solicitada-id": tipo === "solicitada" ? row.id : undefined,
        "data-recibida-id": tipo === "recibida" ? row.id : undefined,
        style: { textAlign: "center", padding: "4px 2px", width: "80px" },
      }}
    />
  );
}

function CostoCell({ row, onSave, onNavigate, disabled, tipo }) {
  const valorCampo = tipo === "pmvb" ? row.pmvb : row.costounitario;
  const [valor, setValor] = useState(String(valorCampo || 0));
  const inputRef = useRef(null);

  useEffect(() => {
    setValor(String(valorCampo || 0));
  }, [valorCampo, row.id]);

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      const nuevoCosto = parseFloat(valor);
      if (valor !== "" && !isNaN(nuevoCosto) && nuevoCosto >= 0 && nuevoCosto !== valorCampo) {
        await onSave(row, { [tipo === "pmvb" ? "pmvb" : "costounitario"]: nuevoCosto });
      }
      onNavigate(row, e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(String(valorCampo || 0));
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={valorCampo || 0}
        disabled
        inputProps={{ style: { textAlign: "right", padding: "4px 2px", width: "80px" } }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      inputRef={inputRef}
      value={valor}
      onClick={(e) => {
        if (e.target && e.target.select) e.target.select();
      }}
      onFocus={(e) => {
        setValor(String(valorCampo || 0));
        setTimeout(() => {
          if (inputRef.current && inputRef.current.select) {
            inputRef.current.select();
          }
        }, 0);
      }}
      onChange={(e) => setValor(e.target.value)}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 0,
        inputMode: "decimal",
        "data-pmvb-id": tipo === "pmvb" ? row.id : undefined,
        "data-costounitario-id": tipo === "costounitario" ? row.id : undefined,
        style: { textAlign: "right", padding: "4px 2px", width: "80px" },
      }}
    />
  );
}

function MedidasCell({ row, medidas, disabled, onSave, onNavigate }) {
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, medidas.length);
  }, [medidas.length]);

  const handleKeyDown = (e, index) => {
    if (e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) {
        if (index > 0) {
          const prev = inputRefs.current[index - 1];
          if (prev) { prev.focus(); if (prev.select) prev.select(); }
        } else {
          onNavigate(row, e);
        }
      } else {
        if (index < medidas.length - 1) {
          const next = inputRefs.current[index + 1];
          if (next) { next.focus(); if (next.select) next.select(); }
        } else {
          onNavigate(row, e);
        }
      }
    } else if (e.key === "ArrowRight") {
      if (index < medidas.length - 1) {
        e.preventDefault();
        const next = inputRefs.current[index + 1];
        if (next) { next.focus(); if (next.select) next.select(); }
      }
    } else if (e.key === "ArrowLeft") {
      if (index > 0) {
        e.preventDefault();
        const prev = inputRefs.current[index - 1];
        if (prev) { prev.focus(); if (prev.select) prev.select(); }
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (index < medidas.length - 1) {
        const next = inputRefs.current[index + 1];
        if (next) { next.focus(); if (next.select) next.select(); }
      } else {
        onNavigate(row, e);
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      onNavigate(row, e);
    }
  };

  if (!medidas || medidas.length === 0) return null;

  return (
    <Box
      data-medidas-id={row.id}
      tabIndex={-1}
      sx={{ display: "flex", gap: "2px", alignItems: "center" }}
    >
      {medidas.map((m, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
          <Box sx={{ fontSize: "0.6rem", color: "#666", mr: "1px", whiteSpace: "nowrap" }}>{m.medida}:</Box>
          <TextField autoComplete="off"
            inputRef={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="decimal"
            enterKeyHint="enter"
            size="small"
            value={m.cantidad}
            disabled={disabled}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              onSave(row, i, val);
            }}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: "50px" }}
            inputProps={{
              style: { fontSize: "0.7rem", textAlign: "center", padding: "2px" },
              "data-medidas-input": `${row.id}-${i}`,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function FrmCompras() {
  const {
    folioCompra,
    handleObtenerDatosCompra,
    handleCargarCompra,
    handleInicializarCompra,
    handleObtenerProveedores,
    listaProveedores,
    listaCorridas,
    handleObtenerCorridas,
    handleRegistrarArticuloCompra,
    handleActualizarPartidaCompra,
    handleEliminarPartidaCompra,
    handleEliminarCompraCompleta,
    handleAplicarCompra,
    handleImprimirEtiquetasCompra,
    handleImprimirCompra,
    handleRefreshDatos,
    handleOpenModalBuscarCompra,
    handleCloseModalBuscarCompra,
    openModalBuscarCompra,
    handleCloseBuscarArticuloSimple,
    openModalBuscarArticulosSimple,
    handleBuscarArticuloSimple,
    partidaSeleccionada,
    handleChangePartidaSeleccionada,
    statusOpenDialog,
    setStatusOpenDialog,
    titleOpenDialog,
    messageOpenDialog,
    statusWaiting,
    listaPartidasCompra,
    totalesCompra,
    statusCompra,
    titleActionSave,
    modoLectura,
    proveedorSeleccionado,
    handleChangeProveedor,
    facturaRemision,
    handleChangeFacturaRemision,
    tipoPago,
    handleChangeTipoPago,
    diasCredito,
    handleChangeDiasCredito,
    observaciones,
    handleChangeObservaciones,
    almacenExhibidorCompra,
    handleChangeAlmacenExhibidorCompra,
    setOpenModalBuscarArticulosSimple,
    handleSelectArticuloSimple,
    openModalReporte,
    pdfUrl,
    setOpenModalReporte,
    handleConfirmDialog,
    totalConLetra,
    handleClickRegresarMainDashboard,
    medidasPartida,
    partidasLoadVersion,
    handleCargarMedidasPartida,
    handleActualizarCantidadMedida,
  } = useCompras();
  const {
    handleObtenerAlmacenesExhibidores,
    listaAlmacenesExhibidoresEntrada,
  } = useInventarios();

  const movimientoBloqueado = statusCompra === "TR" || statusCompra === "CA";

  useEffect(() => {
    window.scrollTo(0, 0);
    handleInicializarCompra();
    handleObtenerProveedores();
    handleObtenerCorridas();
    if (listaAlmacenesExhibidoresEntrada.length === 0) {
      handleObtenerAlmacenesExhibidores("ENTRADA");
    }
  }, []);

  const { parametrosCajaForm, tipoUsuario } = useSesion();
  const { handleOpenAlert } = useLayout();

  const { isLocked: isFormLocked } = useFormTabLock('compras', {
    title: 'Compras',
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || 'Compras'} ya está abierto en otra pestaña`);
    }
  });

  const pcAnfitrionaRef = useRef(parametrosCajaForm.caja?.trim().toUpperCase() || "");
  const getPcAnfitriona = () => parametrosCajaForm.caja?.trim().toUpperCase() || "";
  const [pcTrabajo, setPcTrabajo] = useState(getPcAnfitriona());

  const [proveedorInput, setProveedorInput] = useState(() => {
    return proveedorSeleccionado?.razonsocial || proveedorSeleccionado?.nombrecorto || "";
  });
  const [tipoPagoInput, setTipoPagoInput] = useState(() => tipoPago || "CONTADO");
  const [almacenInventarioInput, setAlmacenInventarioInput] = useState(() => almacenExhibidorCompra || "");
  const [almacenInventarioError, setAlmacenInventarioError] = useState(false);

  const listaProveedoresOrdenada = useMemo(
    () => (listaProveedores ?? []).slice().sort((a, b) => (a.razonsocial || "").localeCompare(b.razonsocial || "")),
    [listaProveedores]
  );

  const listaProveedoresAutoComplete = useMemo(
    () => listaProveedoresOrdenada.map((item) => ({ id: item.claveproveedor, claveproveedor: item.claveproveedor, razonsocial: item.razonsocial, nombrecorto: item.nombrecorto })),
    [listaProveedoresOrdenada]
  );

  const opcionesTipoPago = useMemo(
    () => [
      { id: "CONTADO", tipopago: "CONTADO" },
      { id: "CREDITO", tipopago: "CREDITO" },
    ],
    []
  );

  const listaAlmacenesInventarioOrdenada = useMemo(
    () => (listaAlmacenesExhibidoresEntrada ?? []).slice().sort((a, b) => (a.almacenexhibidor || "").localeCompare(b.almacenexhibidor || "")),
    [listaAlmacenesExhibidoresEntrada]
  );

  const listaAlmacenesInventarioAutoComplete = useMemo(
    () => listaAlmacenesInventarioOrdenada.map((item) => ({ id: item.id, nombre: item.almacenexhibidor })),
    [listaAlmacenesInventarioOrdenada]
  );

  const almacenInventarioSeleccionado = useMemo(
    () => listaAlmacenesInventarioAutoComplete.find((item) => item.nombre === almacenExhibidorCompra) || null,
    [almacenExhibidorCompra, listaAlmacenesInventarioAutoComplete]
  );

  const handleChangeAlmacenInventario = (value) => {
    const nuevoValor = (value || "").trim().toUpperCase();
    handleChangeAlmacenExhibidorCompra(nuevoValor);
    setAlmacenInventarioInput(nuevoValor);
    const esValido = listaAlmacenesInventarioAutoComplete.some((item) => item.nombre.trim().toUpperCase() === nuevoValor);
    setAlmacenInventarioError(!esValido && nuevoValor.length > 0);
  };

  const almacenInventarioValido = useMemo(
    () => listaAlmacenesInventarioAutoComplete.some((item) => item.nombre.trim().toUpperCase() === almacenExhibidorCompra),
    [almacenExhibidorCompra, listaAlmacenesInventarioAutoComplete]
  );

  const aplicarCompraConDestino = () => {
    if (!almacenInventarioValido) {
      setAlmacenInventarioError(true);
      handleOpenAlert("Seleccione un almacén/exhibidor destino vélido antes de aplicar", "warning");
      return;
    }
    handleAplicarCompra(almacenExhibidorCompra);
  };

  useEffect(() => {
    setAlmacenInventarioInput(almacenExhibidorCompra || "");
    if (!almacenExhibidorCompra) {
      setAlmacenInventarioError(false);
      return;
    }
    const esValido = listaAlmacenesInventarioAutoComplete.some(
      (item) => item.nombre.trim().toUpperCase() === almacenExhibidorCompra
    );
    setAlmacenInventarioError(!esValido && listaAlmacenesInventarioAutoComplete.length > 0);
  }, [almacenExhibidorCompra, listaAlmacenesInventarioAutoComplete]);

  const opcionesCorrida = useMemo(
    () => (listaCorridas ?? []).map((item) => item.descripcion),
    [listaCorridas]
  );

  const tieneRegistros = Array.isArray(listaPartidasCompra) && listaPartidasCompra.length > 0;
  const tablaBloqueada = movimientoBloqueado || modoLectura;
  const cabeceroBloqueado = movimientoBloqueado || modoLectura;

  const medidasPartidaRef = useRef(medidasPartida);
  useEffect(() => { medidasPartidaRef.current = medidasPartida; }, [medidasPartida]);

  useEffect(() => {
    if (!listaPartidasCompra || listaPartidasCompra.length === 0) return;
    listaPartidasCompra.forEach((partida) => {
      if (partida.corrida && partida.clavearticulo) {
        handleCargarMedidasPartida(partida);
      }
    });
  }, [partidasLoadVersion]);

  const EDITABLE_FIELDS = useMemo(() => ["corrida", "formula", "pmvb", "solicitada", "recibida", "costounitario", "medidas"], []);

  const handleGridNavigation = useCallback((currentRow, currentField, event) => {
    if (!listaPartidasCompra || listaPartidasCompra.length === 0) return;
    const rowIndex = listaPartidasCompra.findIndex((r) => r.id === currentRow.id);
    if (rowIndex === -1) return;

    let targetRowIndex = rowIndex;
    let targetField = currentField;

    const fieldIndex = EDITABLE_FIELDS.indexOf(currentField);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      targetRowIndex = rowIndex + 1;
      if (targetRowIndex >= listaPartidasCompra.length) targetRowIndex = 0;
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      targetRowIndex = rowIndex - 1;
      if (targetRowIndex < 0) targetRowIndex = listaPartidasCompra.length - 1;
    } else if (event.key === "Tab" || event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      if (event.shiftKey) {
        if (fieldIndex > 0) {
          targetField = EDITABLE_FIELDS[fieldIndex - 1];
        } else {
          targetRowIndex = rowIndex - 1;
          if (targetRowIndex < 0) targetRowIndex = listaPartidasCompra.length - 1;
          targetField = EDITABLE_FIELDS[EDITABLE_FIELDS.length - 1];
        }
      } else {
        if (fieldIndex < EDITABLE_FIELDS.length - 1) {
          targetField = EDITABLE_FIELDS[fieldIndex + 1];
        } else {
          targetRowIndex = rowIndex + 1;
          if (targetRowIndex >= listaPartidasCompra.length) targetRowIndex = 0;
          targetField = EDITABLE_FIELDS[0];
        }
      }
    } else {
      return;
    }

    const nextRow = listaPartidasCompra[targetRowIndex];
    if (nextRow) {
      setTimeout(() => {
        if (targetField === "medidas") {
          const firstInput = document.querySelector(`[data-medidas-input="${nextRow.id}-0"]`);
          if (firstInput) { firstInput.focus(); if (firstInput.select) firstInput.select(); }
        } else {
          const selector = `[data-${targetField}-id="${nextRow.id}"]`;
          const targetInput = document.querySelector(selector);
          if (targetInput) {
            targetInput.focus();
            if (targetInput.select) targetInput.select();
          }
        }
      }, 30);
    }
  }, [listaPartidasCompra, EDITABLE_FIELDS]);

  const handleSelectCompra = async (compra) => {
    const folio = compra?.foliocompra ?? compra?.dFolioCompra ?? compra?.folio;
    const pc = String(compra?.pc ?? compra?.sPc ?? "").trim().toUpperCase();

    if (!folio || !pc) {
      handleOpenAlert("La compra seleccionada no contiene folio y PC", "error");
      return false;
    }

    const cargada = await handleCargarCompra(folio, pc);
    if (!cargada) {
      handleOpenAlert("No se pudieron cargar los datos de la compra seleccionada", "error");
      return false;
    }

    setPcTrabajo(pc);
    return true;
  };

  const handleArticuloSeleccionado = async (data) => {
    await handleRegistrarArticuloCompra(data);
    setOpenModalBuscarArticulosSimple(false);
    handleCloseBuscarArticuloSimple();
  };

  const handleEliminarData = (isConfirmed) => {
    setStatusOpenDialog(false);
    if (isConfirmed) {
      if (titleOpenDialog === "ELIMINAR PARTIDA") {
        handleEliminarPartidaCompra(partidaSeleccionada);
      } else if (titleOpenDialog === "ELIMINAR COMPRA") {
        handleEliminarCompraCompleta();
      }
    }
  };

  const focusFiltroBuscador = () => {
    setTimeout(() => {
      const filtroInput = document.querySelector('input[name="Filtro"]');
      if (filtroInput) {
        filtroInput.focus();
        filtroInput.select();
      }
    }, 150);
  };

  const abrirBuscadorArticulos = () => {
    if (movimientoBloqueado || modoLectura) return;
    setOpenModalBuscarArticulosSimple(true);
    handleBuscarArticuloSimple();
    focusFiltroBuscador();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.key) return;
      const key = String(event.key).trim().toUpperCase();

      if (key === "F4") {
        event.preventDefault();
        event.stopPropagation();
        handleObtenerDatosCompra(0);
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "F1") {
        event.preventDefault();
        event.stopPropagation();
        if (movimientoBloqueado || modoLectura) return;
        setOpenModalBuscarArticulosSimple(true);
        handleBuscarArticuloSimple();
        focusFiltroBuscador();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "A") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneRegistros && !movimientoBloqueado && modoLectura) aplicarCompraConDestino();
        return;
      }

      if (event.altKey && key.trim().toUpperCase() === "I") {
        event.preventDefault();
        event.stopPropagation();
        if (tieneRegistros) handleImprimirCompra();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [almacenInventarioValido, modoLectura, movimientoBloqueado, tieneRegistros]);

  function FormulaInputCell({ row, onSave, onNavigate, disabled }) {
    const [valor, setValor] = useState(row.formula || "");
    const inputRef = useRef(null);

    useEffect(() => {
      setValor(row.formula || "");
    }, [row.formula, row.id]);

    const calcularPrecioSugerido = (formula, costounitario, pmvb) => {
      try {
        let expr = formula
          .replace(/costounitario/gi, String(costounitario || 0))
          .replace(/cuc/gi, String(costounitario || 0))
          .replace(/pmvb/gi, String(pmvb || 0));

        if (/^[+-]?\d+(\.\d+)?$/.test(expr.trim())) {
          const margin = parseFloat(expr) || 0;
          return costounitario * (1 + margin / 100);
        }

        const resultado = parseFloat(eval(expr));
        return isNaN(resultado) ? 0 : resultado;
      } catch (err) {
        return 0;
      }
    };

    const handleKeyDown = async (e) => {
      if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        if (valor !== row.formula) {
          const nuevoPrecioSugerido = calcularPrecioSugerido(valor, row.costounitario, row.pmvb);
          await onSave(row, { formula: valor, preciosugerido: nuevoPrecioSugerido });
        }
        onNavigate(row, e);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setValor(row.formula || "");
        return;
      }
    };

    if (disabled) {
      return (
        <TextField autoComplete="off"
          size="small"
          value={row.formula || ""}
          disabled
          inputProps={{ style: { textAlign: "center", padding: "4px 2px", width: "80px" } }}
        />
      );
    }

    return (
      <TextField autoComplete="off"
        size="small"
        inputRef={inputRef}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onClick={(e) => {
          if (e.target && e.target.select) e.target.select();
        }}
        onFocus={(e) => {
          setValor(row.formula || "");
          setTimeout(() => {
            if (inputRef.current && inputRef.current.select) {
              inputRef.current.select();
            }
          }, 0);
        }}
        onKeyDown={handleKeyDown}
        inputProps={{
          "data-formula-id": row.id,
          style: { textAlign: "center", padding: "4px 2px", width: "80px" }
        }}
      />
    );
  }

  const columns = useMemo(() => [
    { title: "Clave", field: "clavearticulo", width: 100 },
    { title: "Descripción", field: "descripcion", width: 180 },
    {
      title: "Corrida",
      field: "corrida",
      width: 140,
      render: (row) => (
        <FormulaCell
          key={`corrida-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "corrida", e)}
          opcionesCorrida={opcionesCorrida}
        />
      ),
    },
    {
      title: "Fórmula",
      field: "formula",
      width: 100,
      render: (row) => (
        <FormulaInputCell
          key={`formulainput-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "formula", e)}
        />
      ),
    },
    { title: "Precio Sug.", field: "preciosugerido", width: 90, render: (row) => <span>{parseFloat(row.preciosugerido || 0).toFixed(2)}</span> },
    {
      title: "PMVB",
      field: "pmvb",
      width: 90,
      render: (row) => (
        <CostoCell
          key={`pmvb-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "pmvb", e)}
          tipo="pmvb"
        />
      ),
    },
    {
      title: "Cant. Solicitada",
      field: "cantidadsolicitada",
      width: 100,
      render: (row) => (
        <CantidadCell
          key={`solicitada-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "solicitada", e)}
          tipo="solicitada"
        />
      ),
    },
    {
      title: "Cant. Recibida",
      field: "cantidadrecibida",
      width: 100,
      render: (row) => (
        <CantidadCell
          key={`recibida-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "recibida", e)}
          tipo="recibida"
        />
      ),
    },
    {
      title: "Costo Unit.",
      field: "costounitario",
      width: 90,
      render: (row) => (
        <CostoCell
          key={`costounitario-${row.id}`}
          row={row}
          disabled={tablaBloqueada}
          onSave={handleActualizarPartidaCompra}
          onNavigate={(r, e) => handleGridNavigation(r, "costounitario", e)}
          tipo="costounitario"
        />
      ),
    },
    { title: "SubTotal", field: "subtotal", width: 90, render: (row) => <span>{parseFloat(row.subtotal || 0).toFixed(2)}</span> },
    { title: "IVA", field: "iva", width: 80, render: (row) => <span>{parseFloat(row.iva || 0).toFixed(2)}</span> },
    { title: "Total", field: "total", width: 100, render: (row) => <span>{parseFloat(row.total || 0).toFixed(2)}</span> },
    {
      title: "Medidas",
      field: "medidas",
      width: 250,
      render: (row) => (
        <MedidasCell
          key={`medidas-${row.id}`}
          row={row}
          medidas={medidasPartida[row.id] || []}
          disabled={tablaBloqueada}
          onSave={handleActualizarCantidadMedida}
          onNavigate={(r, e) => handleGridNavigation(r, "medidas", e)}
        />
      ),
    },
  ], [tablaBloqueada, opcionesCorrida, handleActualizarPartidaCompra, handleGridNavigation, medidasPartida]);

  return (
    <>
      <Fragment>
        <SizeSoftXLayoutForm
          titlePage="Módulo de Compras"
          buttonActions={
            <MnuComprasActions
              onOpenBuscador={abrirBuscadorArticulos}
              onImprimir={handleImprimirCompra}
              onAplicar={aplicarCompraConDestino}
              onEtiquetar={handleImprimirEtiquetasCompra}
              onOpenBuscadorCompra={handleOpenModalBuscarCompra}
            />
          }
        >
          <div className="conteiner-form-general">
            <div className="conteiner-form-left">
              <div className="body-form">
                <TextField autoComplete="off"
                  name="foliocompra"
                  label="Folio Compra:"
                  value={folioCompra ? folioCompra : 0}
                  sx={{ width: "200px", height: "6.3vh" }}
                  disabled
                />
                <TextField autoComplete="off"
                  name="fecharegistro"
                  label="Fecha:"
                  value={
                    listaPartidasCompra.length > 0
                      ? listaPartidasCompra[0].fecharegistro != null
                        ? getDateStringV2(listaPartidasCompra[0].fecharegistro)
                        : getDateStringV2(Date())
                      : getDateStringV2(Date())
                  }
                  sx={{ width: "250px", height: "6.3vh" }}
                  disabled
                />
                <SemaforoStatus
                  status={
                    listaPartidasCompra.length > 0
                      ? listaPartidasCompra[0].statuscompra
                      : statusCompra
                  }
                />
              </div>

              <div className="body-form">
                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-proveedor"
                  options={listaProveedoresAutoComplete}
                  getOptionLabel={(option) => typeof option === "string" ? option : (option.razonsocial || option.nombrecorto || "")}
                  value={proveedorSeleccionado}
                  inputValue={proveedorInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setProveedorInput(newValue);
                      handleChangeProveedor(event, { razonsocial: newValue, claveproveedor: "" });
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      setProveedorInput(newValue.razonsocial || newValue.nombrecorto || "");
                      handleChangeProveedor(event, newValue);
                      return;
                    }
                    setProveedorInput("");
                    handleChangeProveedor(event, null);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setProveedorInput(newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.claveproveedor === value?.claveproveedor}
                  renderInput={(params) => (
                    <TextField autoComplete="off" {...params} label="Proveedor:" required />
                  )}
                  sx={{ width: "350px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado}
                />

                <TextField autoComplete="off"
                  name="facturaremision"
                  label="Factura/Remisión:"
                  value={facturaRemision || ""}
                  onChange={(e) => handleChangeFacturaRemision(e.target.value)}
                  sx={{ width: "200px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado}
                />

                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-almacen-inventario-compra"
                  options={listaAlmacenesInventarioAutoComplete}
                  getOptionLabel={(option) => typeof option === "string" ? option : option.nombre}
                  value={almacenInventarioSeleccionado}
                  inputValue={almacenInventarioInput}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      handleChangeAlmacenInventario(newValue);
                      return;
                    }
                    if (newValue && typeof newValue === "object") {
                      handleChangeAlmacenInventario(newValue.nombre);
                      return;
                    }
                    handleChangeAlmacenInventario("");
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      handleChangeAlmacenInventario(newInputValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      autoComplete="off"
                      {...params}
                      label="Destino inventario:"
                      required
                      error={almacenInventarioError}
                      helperText={almacenInventarioError ? "Seleccione un almacén/exhibidor vélido" : ""}
                    />
                  )}
                  sx={{ width: "280px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado}
                />
              </div>

              <div className="body-form">
                <Autocomplete
                  freeSolo
                  noOptionsText="Sin resultados"
                  openOnFocus
                  id="id-tipopago"
                  options={opcionesTipoPago}
                  getOptionLabel={(option) => typeof option === "string" ? option : option.tipopago}
                  value={opcionesTipoPago.find(t => t.tipopago === tipoPago) || { tipopago: tipoPago }}
                  inputValue={tipoPagoInput}
                  onChange={(event, newValue) => {
                    const tipopago = typeof newValue === "string" ? newValue : (newValue?.tipopago || "CONTADO");
                    handleChangeTipoPago(tipopago);
                    setTipoPagoInput(tipopago);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setTipoPagoInput(newInputValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField autoComplete="off" {...params} label="Tipo Pago:" />
                  )}
                  sx={{ width: "180px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado}
                />

                <TextField autoComplete="off"
                  name="diascredito"
                  label="Días Crédito:"
                  type="text"
                  inputMode="numeric"
                  enterKeyHint="enter"
                  value={diasCredito || 0}
                  onChange={(e) => handleChangeDiasCredito(parseInt(e.target.value, 10) || 0)}
                  sx={{ width: "150px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado || tipoPago !== "CREDITO"}
                />

                <TextField autoComplete="off"
                  name="observaciones"
                  label="Observaciones:"
                  value={observaciones || ""}
                  onChange={(e) => handleChangeObservaciones(e.target.value)}
                  multiline
                  rows={1}
                  sx={{ width: "400px", height: "6.3vh" }}
                  disabled={cabeceroBloqueado}
                />
              </div>

              <div className="body-Detalles-entradas-salidas">
                <SizeSoftXTableVentas
                  columns={columns}
                  data={listaPartidasCompra == null ? [] : listaPartidasCompra}
                  id="id"
                  row={partidaSeleccionada}
                  setRow={handleChangePartidaSeleccionada}
                  fontSize="small"
                  rowsPerPageIntro={15}
                />
              </div>

              <div className="body-pie-entradas-salidas">
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="totalregistros"
                    label="Total registros:"
                    value={totalesCompra.totalRegistros}
                    sx={{ width: "150px", height: "7.2vh" }}
                    disabled
                  />
                </div>
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="cantidadarticulos"
                    label="Cantidad artículos:"
                    value={totalesCompra.cantidadArticulos}
                    sx={{ width: "180px", height: "7.2vh" }}
                    disabled
                  />
                </div>
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="subtotalcompra"
                    label="SubTotal:"
                    value={totalesCompra.subTotal.toFixed(2)}
                    sx={{ width: "180px", height: "7.2vh" }}
                    disabled
                  />
                </div>
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="totaliva"
                    label="Total IVA:"
                    value={totalesCompra.totalIVA.toFixed(2)}
                    sx={{ width: "180px", height: "7.2vh" }}
                    disabled
                  />
                </div>
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="totalcompra"
                    label="Total Compra:"
                    value={totalesCompra.total.toFixed(2)}
                    sx={{ width: "200px", height: "7.2vh" }}
                    disabled
                  />
                </div>
                <div className="detalles-entrada-salida">
                  <TextField autoComplete="off"
                    name="totalconletra"
                    label="Total en letra:"
                    value={totalConLetra}
                    sx={{ width: "400px", height: "7.2vh" }}
                    disabled
                    multiline
                    InputProps={{
                      sx: { padding: "5px" }
                    }}
                  />
                </div>
              </div>

            </div>
          </div>
        </SizeSoftXLayoutForm>

        <ModalBuscarArticulosSimple
          openModal={openModalBuscarArticulosSimple && !movimientoBloqueado}
          handleCloseModal={() => {
            setOpenModalBuscarArticulosSimple(false);
            handleCloseBuscarArticuloSimple();
          }}
          handleArticuloSelect={handleArticuloSeleccionado}
        />

        <ModalBuscarCompras
          openModal={openModalBuscarCompra}
          handleCloseModal={handleCloseModalBuscarCompra}
          onSelectCompra={handleSelectCompra}
        />

        <Modal open={openModalReporte} onClose={() => setOpenModalReporte(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "82vw",
              height: "84vh",
              maxWidth: "1100px",
              background: "white",
              padding: 2,
              boxShadow: 24,
              outline: "none",
            }}
          >
            {pdfUrl && <iframe title="Vista previa compra" src={pdfUrl} width="100%" height="100%" />}
          </Box>
        </Modal>

        <SizeSoftXConfirmDialog
          open={statusOpenDialog}
          title={titleOpenDialog}
          message={messageOpenDialog}
          handleConfirmDialog={handleEliminarData}
        />

        <SizeSoftXAlert />

        <SizeSoftXLoadingProcess
          open={statusWaiting}
          message="Procesando información, por favor espere..."
        />
      </Fragment>
    </>
  );
}